// components/BrokerTailSearch.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function BrokerTailSearch({
  className = "",
  initial = "",
  placeholder = "Search tail #",
}) {
  const [value, setValue] = useState(initial);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]); // autocomplete list
  const nav = useNavigate();
  const boxRef = useRef(null);

  // simple debounce
  const debounced = useMemo(() => value.trim(), [value]);

  useEffect(() => {
    let alive = true;
    async function run() {
      const q = debounced;
      if (!q) {
        setItems([]);
        return;
      }
      setLoading(true);

      // query distinct tail numbers that match
      const { data, error } = await supabase
        .from("project_status")
        .select("tail_number")
        .ilike("tail_number", `%${q}%`)
        .not("tail_number", "is", null)
        .limit(8);

      if (!alive) return;
      setLoading(false);
      if (error) {
        console.error("tail search error:", error);
        setItems([]);
        return;
      }
      // unique, keep order
      const uniq = [...new Map((data || []).map(d => [d.tail_number, d])).keys()];
      setItems(uniq);
      setOpen(true);
    }
    const t = setTimeout(run, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [debounced]);

  // close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  function go(tail) {
    setOpen(false);
    setValue(tail);
    // deep-link to Projects with query param ?tail=XXXX
    nav(`/brokerprojects?tail=${encodeURIComponent(tail)}`);
  }

  function clear() {
    setValue("");
    setItems([]);
    setOpen(false);
  }

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)] px-3 py-2 flex items-center gap-2">
        <Search className="w-4 h-4 shrink-0 text-black/35" strokeWidth={2} />
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => value && setOpen(true)}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none text-sm placeholder-black/50"
          aria-label="Search tail number"
        />
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin opacity-70" />
        ) : value ? (
          <button onClick={clear} aria-label="Clear" className="opacity-70 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>

      {open && items.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-white/15 bg-white/80 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)] overflow-hidden">
          {items.map((t) => (
            <button
              key={t}
              onClick={() => go(t)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-black/[0.04]"
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {open && !loading && items.length === 0 && debounced && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-black/10 bg-white shadow-lg px-3 py-2 text-sm text-black/60">
          No matching tail numbers
        </div>
      )}
    </div>
  );
}
